import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import useSWR from 'swr';
import {
  EndorsementsByExperienceId,
  endorsementsForUserKey,
  fetchEndorsementsByExperienceIdSWR,
} from '@services/endorsements/api';
import { CvDetailExperiences } from '@components/templates/cv/interface/CvDetailInterface';
import { EndorsementCard } from './EndorsementCard';
import { experienceLabelsById } from './experienceLabels';

interface EndorsementsSectionProps {
  slug: string | undefined;
  experiences: CvDetailExperiences[] | undefined;
}

/**
 * All of a candidate's references in one always-open list on the recruiter
 * CV view (under the Bestie evaluation). They used to sit collapsed under each
 * experience, and only the first two experiences render by default, so most
 * were never seen. Renders nothing when there are none.
 */
export const EndorsementsSection = ({ slug, experiences }: EndorsementsSectionProps) => {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language || 'ro';

  const { data: endorsementsByExperienceId } = useSWR<EndorsementsByExperienceId>(
    endorsementsForUserKey(slug, locale),
    fetchEndorsementsByExperienceIdSWR,
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  const endorsements = useMemo(
    () => Object.values(endorsementsByExperienceId ?? {})
      .flat()
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [endorsementsByExperienceId],
  );
  const labels = useMemo(() => experienceLabelsById(experiences), [experiences]);

  if (endorsements.length === 0) {
    return null;
  }

  return (
    <section id="references" className="my-6">
      <h6 className="mb-3 text-lg font-semibold">
        {t('endorsement.section.header', 'References')}
        {' '}
        <span className="text-ink-medium">({endorsements.length})</span>
      </h6>
      <div className="flex flex-col gap-3">
        {endorsements.map((endorsement) => (
          <EndorsementCard
            key={endorsement.id}
            endorsement={endorsement}
            experienceLabel={labels[String(endorsement.experienceId)]}
          />
        ))}
      </div>
    </section>
  );
};
